import { FormHelperText } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersTextField } from "@mui/x-date-pickers/PickersTextField";
import { Dayjs } from "dayjs";
import React, { useEffect, useRef } from "react";
import { Control, Controller, Path } from "react-hook-form";
import { DATE_FORMAT } from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { getHelperText, removeWhiteSpace } from "../../helpers/utils";
import { MuiBox } from "../box/MuiBox";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiDateFieldProps, OpenPickerIcon } from "./MuiDateField";
import { MuiDateFieldWrapper } from "./MuiDateField.style";
import { MuiInfoToolTip } from "../tooltip/infoToolTip/MuiInfoToolTip";

interface MuiDateFieldFormControllerProps extends MuiDateFieldProps {
  control: Control<any>;
  name: Path<any>;
  format?: string;
  id?: string;
  disableManualInput?: boolean;
  requiredARIALabel: string;
  selectDateARIALabel: string;
}

export const MuiDateFieldFormController: React.FC<MuiDateFieldFormControllerProps> = ({
  onChange,
  control,
  name,
  format = DATE_FORMAT.DD_MM_YYYY_SLASH,
  id = COMPONENT_IDS.COMMON_DATE_PICKER_FORM_CONTROLLER,
  label,
  isMandatory,
  disableLabel,
  isErrorIconEnabled = true,
  valueFormat,
  disableManualInput = false,
  clearable = false,
  requiredARIALabel = "Required",
  selectDateARIALabel = "Select",
  hideCalendarIconOnValue = false,
  infoMessage,
  ...rest
}) => {
  const datePickerRef = useRef<HTMLDivElement>(null);

  const pickerRef = useRef<HTMLDivElement>(null);
  const ignoreBlurRef = useRef(false);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // If click is inside calendar popper or on picker icon → skip blur
      if (
        datePickerRef.current?.contains(target) ||
        target.closest(".MuiPickersPopper-root") ||
        target.closest(".MuiPickersDay-root") ||
        target.closest(".MuiIconButton-root") ||
        target.closest(".MuiPickersCalendarHeader-root")
      ) {
        ignoreBlurRef.current = true;
        setTimeout(() => (ignoreBlurRef.current = false), 200);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  return (
    <MuiDateFieldWrapper>
      {label && (
        <MuiBox className={"title"}>
          <MuiTypography
            className={disableLabel ? "mui-label disabled" : "mui-label"}
            component="label"
            variant="h3"
          >
            {label}{" "}
            {isMandatory && (
              <label className="asterisk" aria-label={requiredARIALabel}>
                *
              </label>
            )}
          </MuiTypography>
          {infoMessage && (
            <MuiBox className="tooltip-container">
              <MuiInfoToolTip
                message={infoMessage}
                placement="top"
                className="tooltip-wrapper"
              />
            </MuiBox>
          )}
        </MuiBox>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState: { error, invalid } }) => (
            <div ref={pickerRef}>
              <DatePicker
                format={format}
                enableAccessibleFieldDOMStructure
                slots={{
                  textField: PickersTextField,
                  openPickerIcon: OpenPickerIcon,
                }}
                slotProps={{
                  textField: {
                    id: id,
                    inputProps: {
                      "data-testid": id,
                      "aria-label": `${label} ${isMandatory && !disableLabel ? requiredARIALabel : ""}`,
                    },
                    ref: datePickerRef,
                    error: invalid,
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      if (ignoreBlurRef.current) {
                        // skip blur triggered by picker icon or popper click
                        return;
                      }
                      field?.onBlur?.();
                    },
                  },
                  field: {
                    readOnly: disableManualInput,
                    clearable,
                  },
                  popper: {
                    "aria-label": `${selectDateARIALabel} ${label}`,
                  },
                  openPickerIcon: { hideCalendarIconOnValue },
                  ...rest.slotProps,
                }}
                {...rest}
                onChange={(selectedDate: Dayjs | null) => {
                  onChange?.(selectedDate);
                  if (valueFormat && selectedDate) {
                    field.onChange(selectedDate.format(valueFormat));
                  } else {
                    field.onChange(selectedDate);
                  }
                }}
              />
              {error && (
                <FormHelperText error={invalid}>
                  {error?.message && (
                    <MuiBox
                      role="alert"
                      aria-description={getHelperText(
                        error?.message,
                        label as string,
                      )}
                      className="error-icon"
                    >
                      {isErrorIconEnabled && <ErrorCrossIcon />}
                      <div
                        aria-hidden
                        id={`helper-text-${removeWhiteSpace(label)}`}
                      >
                        {getHelperText(error?.message, label as string)}
                      </div>
                    </MuiBox>
                  )}
                </FormHelperText>
              )}
            </div>
          )}
        />
      </LocalizationProvider>
    </MuiDateFieldWrapper>
  );
};
